import request from '@teetsh/app/src/utils/request';
import { saveAs } from 'file-saver';
import { useState } from 'react';
import { useSchoolSchoolyear } from '@teetsh/app/src/containers/App/context/SchoolyearSchoolProvider';
import { useTranslation } from 'react-i18next';
import useNotification from 'containers/App/Notifications/useNotification';
import { UserLocale } from '@teetsh/app/src/types/user';
import {
  Planner,
  PlannerCalendarPDFSettings,
  PlannerCategoriesPDFSettings,
} from '@teetsh/app/src/types/planner';
import { buildPlannerCalendarPDFParams } from 'containers/Planner/Main/Calendar/PreviewPDF/Sidebar/buildPlannerCalendarPDFParams';
import { buildPlannerCategoriesPDFParams } from 'containers/Planner/Main/Categories/PreviewPDF/Sidebar/buildPlannerCategoriesPDFParams';

interface Props {
  schoolyearId: string;
  schoolId: string;
  onStart: () => void;
  onFinished: () => void;
  onError: () => void;
  planner: Planner;
  lng: UserLocale;
  settings: PlannerCalendarPDFSettings | PlannerCategoriesPDFSettings;
  date?: Date;
  chronologicalOrder?: boolean;
  selectedCategories?: string[];
}

function fetchPlannerAPI(s3Url: string, plannerName: string) {
  return fetch(s3Url)
    .then(response => response.blob())
    .then(response => {
      saveAs(response, `${plannerName}.pdf`);
    });
}

async function downloadPlanner(props: Props) {
  const {
    schoolyearId,
    schoolId,
    onStart,
    onFinished,
    onError,
    planner,
    lng,
    settings,
    date,
    chronologicalOrder,
    selectedCategories,
  } = props;

  onStart();

  let requestURL = `/schoolyears/${schoolyearId}/schools/${schoolId}/planners/${planner.id}`;
  if (date) {
    const params = buildPlannerCalendarPDFParams({
      planner,
      lng,
      date,
      selectedCategories,
      settings,
    });
    requestURL = `${requestURL}/calendar/pdf?${params.toString()}`;
  } else {
    const params = buildPlannerCategoriesPDFParams({
      planner,
      lng,
      chronologicalOrder: !!chronologicalOrder,
      selectedCategories,
      settings,
    });
    requestURL = `${requestURL}/categories/pdf?${params.toString()}`;
  }
  try {
    const response = await request(requestURL, {
      method: 'GET',
    });
    const token = response.pdf_token;
    await fetchPlannerAPI(token, planner.title);
  } catch {
    onError();
  } finally {
    onFinished();
  }
}

export default function useDownloadPlanner() {
  const { schoolyear, school } = useSchoolSchoolyear();
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const { notifyError } = useNotification();
  const { t } = useTranslation(['common']);

  const download = ({
    planner,
    lng,
    settings,
    date,
    chronologicalOrder,
    selectedCategories,
  }: {
    planner: Planner;
    lng: UserLocale;
    settings: PlannerCalendarPDFSettings | PlannerCategoriesPDFSettings;
    date?: Date;
    chronologicalOrder?: boolean;
    selectedCategories?: string[];
  }) =>
    downloadPlanner({
      planner,
      schoolyearId: schoolyear.id,
      schoolId: school.id,
      onStart: () => setIsDownloading(true),
      onFinished: () => setIsDownloading(false),
      onError: () => notifyError({ title: t('common:messages.genericError') }),
      lng,
      settings,
      date,
      chronologicalOrder,
      selectedCategories,
    });

  return {
    download,
    isDownloading,
  };
}
